"use client";

import { Languages } from "lucide-react";
import { useId } from "react";
import { useToolStrings } from "@/components/citation-tool/tool-strings";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { OUTPUT_LOCALES } from "@/lib/output-locales";

interface OutputLanguageSelectProps {
  value: string;
  onChange: (lang: string) => void;
}

/**
 * Selector for the citation OUTPUT language (localized CSL terms like n.d./In/Ed.).
 * Scoped to the offline locales in {@link OUTPUT_LOCALES} — five bundled with citeproc,
 * twelve vendored — so picking one never costs a network round trip. Defaults to
 * English, the unchanged formatter path, except on a localized page, which starts on the
 * language matching the page. This is citation output only: it changes "n.d." to "s. f.",
 * not the buttons around it.
 */
export function OutputLanguageSelect({
  value,
  onChange,
}: OutputLanguageSelectProps) {
  const s = useToolStrings();
  const labelId = useId();

  return (
    <div className="flex items-center gap-2">
      {/* A <span> (not <label>): the control is a Radix Select, named via
          aria-labelledby, so a native <label> association does not apply. */}
      <span
        className="flex shrink-0 items-center gap-1.5 font-medium text-muted-foreground text-sm"
        id={labelId}
      >
        <Languages aria-hidden="true" className="size-4" />
        {s.outputLanguage}
      </span>
      <Select onValueChange={onChange} value={value}>
        <SelectTrigger
          aria-labelledby={labelId}
          className="w-full min-w-[10rem]"
          size="sm"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {OUTPUT_LOCALES.map((locale) => (
            <SelectItem key={locale.code} value={locale.code}>
              {locale.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
